// Pure helpers for the Problemen explorer: query strings for the two admin
// endpoints, chip labels and filter arithmetic. Kept free of React so the
// logic is unit-testable in the node Vitest environment.
import { CLASS_LABEL, SURFACE_LABEL, type ExplorerFilters, type TelemetryMessage } from "./types";

export const DEFAULT_FILTERS: ExplorerFilters = { kind: "error" };

export type ChipKey = Exclude<keyof ExplorerFilters, "kind">;

const CHIP_ORDER: ChipKey[] = ["error_class", "source", "screen", "surface", "date", "user", "detail"];

function commonParams(f: ExplorerFilters, days: number): URLSearchParams {
  const p = new URLSearchParams();
  p.set("kind", f.kind);
  p.set("days", String(days));
  return p;
}

function appendFilters(p: URLSearchParams, f: ExplorerFilters): void {
  if (f.error_class !== undefined) p.set("error_class", f.error_class);
  if (f.source !== undefined) p.set("source", f.source);
  if (f.screen !== undefined) p.set("screen", f.screen);
  if (f.surface !== undefined) p.set("surface", f.surface);
  if (f.date !== undefined) p.set("date", f.date);
  if (f.user !== undefined) p.set("user_id", f.user.id);
}

export function eventsQuery(f: ExplorerFilters, days: number, beforeId?: number | null, limit = 100): string {
  const p = commonParams(f, days);
  p.set("limit", String(limit));
  appendFilters(p, f);
  if (f.detail !== undefined) p.set("detail", f.detail);
  if (beforeId !== undefined && beforeId !== null) p.set("before_id", String(beforeId));
  return `/api/admin/telemetry/events?${p.toString()}`;
}

export function messagesQuery(f: ExplorerFilters, days: number): string {
  const p = commonParams(f, days);
  appendFilters(p, f);
  return `/api/admin/telemetry/messages?${p.toString()}`;
}

function chipLabel(f: ExplorerFilters, key: ChipKey): string | null {
  switch (key) {
    case "error_class":
      return f.error_class === undefined ? null : `klasse: ${CLASS_LABEL[f.error_class] ?? f.error_class}`;
    case "source":
      return f.source === undefined ? null : `bron: ${f.source}`;
    case "screen":
      return f.screen === undefined ? null : `scherm: ${f.screen}`;
    case "surface":
      return f.surface === undefined ? null : `app: ${SURFACE_LABEL[f.surface]}`;
    case "date":
      return f.date === undefined ? null : `dag: ${f.date}`;
    case "user":
      return f.user === undefined ? null : `gebruiker: ${f.user.email || f.user.id.slice(0, 8)}`;
    case "detail":
      if (f.detail === undefined) return null;
      return f.detail === "" ? "zonder melding" : `melding: ${f.detail}`;
  }
}

export function filterChips(f: ExplorerFilters): { key: ChipKey; label: string }[] {
  const out: { key: ChipKey; label: string }[] = [];
  for (const key of CHIP_ORDER) {
    const label = chipLabel(f, key);
    if (label !== null) out.push({ key, label });
  }
  return out;
}

export function withoutFilter(f: ExplorerFilters, key: ChipKey): ExplorerFilters {
  const next: ExplorerFilters = { ...f };
  delete next[key];
  return next;
}

export function mergeFilters(f: ExplorerFilters, patch: Partial<ExplorerFilters>): ExplorerFilters {
  const next: ExplorerFilters = { ...f, ...patch };
  if (next.kind === undefined) next.kind = f.kind;
  for (const key of CHIP_ORDER) {
    if (next[key] === undefined) delete next[key];
  }
  return next;
}

export function messageKey(m: Pick<TelemetryMessage, "error_class" | "source" | "detail">): string {
  return `${m.error_class} ${m.source} ${m.detail ?? ""}`;
}
